/**
 * Section 4: Property Condition + Details
 */

import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { Select, Textarea } from '@ox/visits-ui/components/shared/FormComponents';

export function Section4Condition() {
  const { formData, updateField } = useForm();

  const handleSprinklerSystemChange = useCallback((value: string) => {
    updateField('sprinkler_system', value);
  }, [updateField]);

  const handleOdorsPresentChange = useCallback((value: string) => {
    updateField('odors_present', value);
  }, [updateField]);

  const handleOdorsDescriptionChange = useCallback((value: string) => {
    updateField('odors_description', value);
  }, [updateField]);

  const handleRoofDamageChange = useCallback((value: string) => {
    updateField('roof_damage', value);
  }, [updateField]);

  const handleRoofDamageDescriptionChange = useCallback((value: string) => {
    updateField('roof_damage_description', value);
  }, [updateField]);

  const handleDryrotInsectsChange = useCallback((value: string) => {
    updateField('dryrot_insects', value);
  }, [updateField]);

  const handleDryrotInsectsDescriptionChange = useCallback((value: string) => {
    updateField('dryrot_insects_description', value);
  }, [updateField]);

  const handleStreetRestrictionsChange = useCallback((value: string) => {
    updateField('street_restrictions', value);
  }, [updateField]);

  const handleStreetRestrictionsDescriptionChange = useCallback((value: string) => {
    updateField('street_restrictions_description', value);
  }, [updateField]);

  const handleAtticCrawlspaceChange = useCallback((value: string) => {
    updateField('attic_crawlspace', value);
  }, [updateField]);

  const handleAtticCrawlspaceDescriptionChange = useCallback((value: string) => {
    updateField('attic_crawlspace_description', value);
  }, [updateField]);

  const handleObviousDamageChange = useCallback((value: string) => {
    updateField('obvious_damage', value);
  }, [updateField]);

  const handleWaterFunctioningChange = useCallback((value: string) => {
    updateField('water_functioning', value);
  }, [updateField]);

  const handleWaterFunctioningDescriptionChange = useCallback((value: string) => {
    updateField('water_functioning_description', value);
  }, [updateField]);

  return (
    <div className="form-section">
      <h2>Property Condition</h2>

      <Select
        label="Did you observe a sprinkler system on the property?"
        name="sprinkler_system"
        value={
          formData.sprinkler_system || ''
        }
        onChange={handleSprinklerSystemChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />

      <Select
        label="Are there any odors present in the home?"
        name="odors_present"
        value={
          formData.odors_present || ''
        }
        onChange={handleOdorsPresentChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes - pet odor in living room', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />
      {formData.odors_present === 'Yes' && (
        <Textarea
          label="Describe the odors present"
          name="odors_description"
          value={formData.odors_description || ''}
          onChange={handleOdorsDescriptionChange}
          placeholder="Describe the odors present in the home"
          autoSize
          required
        />
      )}

      <Select
        label="Was there any visible roof damage?"
        name="roof_damage"
        value={
          formData.roof_damage || ''
        }
        onChange={handleRoofDamageChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />
      {formData.roof_damage === 'Yes' && (
        <Textarea
          label="Describe the roof damage"
          name="roof_damage_description"
          value={formData.roof_damage_description || ''}
          onChange={handleRoofDamageDescriptionChange}
          placeholder="Describe the roof damage"
          autoSize
          required
        />
      )}

      <Select
        label="Was there any visible home damage possibly related to dry rot, insects, etc.?"
        name="dryrot_insects"
        value={
          formData.dryrot_insects || ''
        }
        onChange={handleDryrotInsectsChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes - termite damage in garage', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />
      {formData.dryrot_insects === 'Yes' && (
        <Textarea
          label="Describe home damage possibly related to dry rot, insects, etc."
          name="dryrot_insects_description"
          value={formData.dryrot_insects_description || ''}
          onChange={handleDryrotInsectsDescriptionChange}
          placeholder="Describe the home damage possibly related to dry rot, insects, etc."
          autoSize
          required
        />
      )}

      <Select
        label="Where there any restrictions to access related to street condition?"
        name="street_restrictions"
        value={
          formData.street_restrictions || ''
        }
        onChange={handleStreetRestrictionsChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes - unpaved road', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />
      {formData.street_restrictions === 'Yes' && (
        <Textarea
          label="Describe the street restrictions"
          name="street_restrictions_description"
          value={formData.street_restrictions_description || ''}
          onChange={handleStreetRestrictionsDescriptionChange}
          placeholder="Describe the street restrictions"
          autoSize
          required
        />
      )}

      <Select
        label="Did attics or crawl spaces have adequate access?"
        name="attic_crawlspace"
        value={
          formData.attic_crawlspace || ''
        }
        onChange={handleAtticCrawlspaceChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
          { label: 'Not Applicable', value: 'Not Applicable' }
        ]}
        required
      />
      {formData.attic_crawlspace === 'No' && (
        <Textarea
          label="Describe attics or crawl spaces accessible"
          name="attic_crawlspace_description"
          value={formData.attic_crawlspace_description || ''}
          onChange={handleAtticCrawlspaceDescriptionChange}
          placeholder="Describe the attics or crawl spaces accessible"
          autoSize
          required
        />
      )}

      <Select
        label="Were toilets, faucets, major water supplies in working order?"
        name="water_functioning"
        value={formData.water_functioning || ''}
        onChange={handleWaterFunctioningChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />
      {formData.water_functioning === 'No' && (
        <Textarea
          label="Describe the issues with toilets, faucets, major water supplies"
          name="water_functioning_description"
          value={formData.water_functioning_description || ''}
          onChange={handleWaterFunctioningDescriptionChange}
          placeholder="Describe the issues with toilets, faucets, major water supplies"
          autoSize
          required
        />
      )}

      <Textarea
        label="Is there any obvious damage to the home that may impact value or marketability?"
        name="obvious_damage"
        value={formData.obvious_damage || ''}
        onChange={handleObviousDamageChange}
        placeholder="Describe any obvious damage to the home that may impact value or marketability"
        maxLength={1000}
        autoSize
      />
    </div>
  );
}
